import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = React.useState(true);
  const [hasCheckedStatus, setHasCheckedStatus] = React.useState(false);

  console.log('ProtectedRoute - authLoading:', authLoading, 'user:', user?.id, 'hasCheckedStatus:', hasCheckedStatus);

  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || hasCheckedStatus) return;

      try {
        setIsCheckingAdmin(true);
        
        // Check if user is a team member (has a role assigned)
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Error checking user role:', userError);
          setIsAdmin(false);
          return;
        }

        if (userData && userData.role) {
          console.log('ProtectedRoute - User is a team member with role:', userData.role);
          setIsAdmin(true);
          setHasCheckedStatus(true);
          
          // Store the result in sessionStorage to prevent re-checking
          sessionStorage.setItem('user-admin-status', JSON.stringify({
            userId: user.id,
            isAdmin: true,
            timestamp: Date.now()
          }));
          
          return;
        }

        // User is not a team member, check subscription status
        console.log('ProtectedRoute - User is not a team member, checking subscription');
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('user_subscriptions')
          .select('status')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();

        if (subscriptionError) {
          console.error('Error checking subscription:', subscriptionError);
          setIsAdmin(false);
        } else if (subscriptionData) {
          console.log('ProtectedRoute - User has active subscription');
          setIsAdmin(false);
        } else {
          console.log('ProtectedRoute - User has no active subscription');
          setIsAdmin(false);
        }
        
        setHasCheckedStatus(true);
        
        // Store the result in sessionStorage to prevent re-checking
        sessionStorage.setItem('user-admin-status', JSON.stringify({
          userId: user.id,
          isAdmin: false,
          timestamp: Date.now()
        }));
        
      } catch (error) {
        console.error('Error in checkAdminStatus:', error);
        setIsAdmin(false);
        setHasCheckedStatus(true);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    // Check if we have a cached result in sessionStorage
    const cachedStatus = sessionStorage.getItem('user-admin-status');
    if (cachedStatus && user) {
      try {
        const parsed = JSON.parse(cachedStatus);
        // Use cached result if it's for the same user and less than 5 minutes old
        if (parsed.userId === user.id && (Date.now() - parsed.timestamp) < 5 * 60 * 1000) {
          console.log('ProtectedRoute - Using cached admin status:', parsed.isAdmin);
          setIsAdmin(parsed.isAdmin);
          setHasCheckedStatus(true);
          setIsCheckingAdmin(false);
          return;
        }
      } catch (e) {
        console.error('Error parsing cached status:', e);
        sessionStorage.removeItem('user-admin-status');
      }
    }

    // Only check if we haven't checked before and have a user
    if (user && !hasCheckedStatus) {
      checkAdminStatus();
    }
  }, [user, hasCheckedStatus]);

  // Show loading while checking admin status
  if (isCheckingAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Checking your access...</h2>
          <p className="text-gray-600">Please wait while we verify your permissions.</p>
        </div>
      </div>
    );
  }

  // Show loading while auth is loading
  if (authLoading) {
    console.log('ProtectedRoute - Auth is loading, showing loading screen');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading authentication...</h2>
          <p className="text-gray-600">Please wait while we verify your identity.</p>
        </div>
      </div>
    );
  }

  // If no user, redirect to login
  if (!user) {
    console.log('ProtectedRoute - No user, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If admin access is required but user is not admin
  if (requireAdmin && !isAdmin) {
    console.log('ProtectedRoute - Admin access required but user is not admin, checking subscription for redirection');
    
    // Check if user has an active subscription to determine where to redirect
    const checkSubscriptionAndRedirect = async () => {
      try {
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('user_subscriptions')
          .select('status')
          .eq('user_id', user?.id)
          .eq('status', 'active')
          .single();

        if (subscriptionError) {
          console.error('Error checking subscription for redirection:', subscriptionError);
          navigate('/subscription', { replace: true });
          return;
        }

        if (subscriptionData) {
          console.log('ProtectedRoute - Redirecting non-admin user with subscription to dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          console.log('ProtectedRoute - Redirecting non-admin user without subscription to subscription page');
          navigate('/subscription', { replace: true });
        }
      } catch (error) {
        console.error('Error in subscription check for redirection:', error);
        navigate('/subscription', { replace: true });
      }
    };

    // Call the function immediately
    checkSubscriptionAndRedirect();
    
    // Show loading while redirecting
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Redirecting...</h2>
          <p className="text-gray-600">Please wait while we redirect you to the appropriate page.</p>
        </div>
      </div>
    );
  }

  console.log('ProtectedRoute - User authenticated, rendering children');
  return <>{children}</>;
};

export default ProtectedRoute; 